import { fieldDef, fieldsOf, type CrmEntity } from "./fields";
import type { CrmLayout } from "./api";
import { formatPhoneDisplay, isValidEmail, isValidPhone, maskCep, phoneToE164 } from "@/lib/masks";
import { isCpfChecksumValid } from "@/lib/cpf";
import { isCnpjValid } from "@/lib/cnpj";

/** Values being edited on a form: strings for inputs, booleans/numbers where the API uses them. */
export type Draft = Record<string, any>;

/** CEP mask only for Brazilian-looking input; international codes keep what was typed. */
export const maskPostal = (v: string) => (/^[\d\s-]*$/.test(v) ? maskCep(v) : v);

export function toDraft(entity: CrmEntity, values: Record<string, any>): Draft {
  const d: Draft = {};
  for (const def of fieldsOf(entity)) {
    const raw = values[def.key];
    switch (def.type) {
      case "phone": d[def.key] = raw ? formatPhoneDisplay(String(raw)) : ""; break;
      case "number": d[def.key] = raw === null || raw === undefined ? "" : String(raw); break;
      case "boolean": d[def.key] = raw === undefined ? (def.key === "isActive") : !!raw; break;
      case "decimal": d[def.key] = typeof raw === "number" ? raw : null; break;
      case "datetime": d[def.key] = raw || null; break;
      case "lookup": d[def.key] = raw || null; break;
      default: d[def.key] = raw === null || raw === undefined ? "" : String(raw);
    }
  }
  return d;
}

/** Converts one draft value to what the API accepts. */
function toApiValue(entity: CrmEntity, key: string, value: any): any {
  const def = fieldDef(entity, key);
  if (!def) return value;
  switch (def.type) {
    case "phone": {
      const text = String(value ?? "").trim();
      return text ? (phoneToE164(text) ?? text) : null;
    }
    case "boolean": return !!value;
    case "decimal": return typeof value === "number" && Number.isFinite(value) ? value : null;
    case "lookup": return value || null;
    case "datetime": return undefined;
    default: {
      const text = String(value ?? "").trim();
      return text === "" ? null : text;
    }
  }
}

const same = (a: any, b: any) => (a ?? "") === (b ?? "");

/** Values for POST/PUT: only what changed (or everything filled in for a new record). */
export function changedValues(entity: CrmEntity, initial: Draft | null, draft: Draft): Record<string, any> {
  const out: Record<string, any> = {};
  for (const def of fieldsOf(entity)) {
    if (def.readOnly) continue;
    const key = def.key;
    if (initial ? same(initial[key], draft[key]) : (draft[key] === "" || draft[key] === null || draft[key] === undefined)) continue;
    const v = toApiValue(entity, key, draft[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

export const isDirty = (entity: CrmEntity, initial: Draft | null, draft: Draft) =>
  Object.keys(changedValues(entity, initial, draft)).length > 0;

const URL_RE = /^(https?:\/\/)?[^\s/$.?#]+\.[^\s]+$/i;

/** Client-side checks that mirror the API (the API stays the authority). Returns { field: code }. */
export function validateDraft(entity: CrmEntity, draft: Draft, layout: CrmLayout | undefined): Record<string, string> {
  const errors: Record<string, string> = {};
  const empty = (k: string) => String(draft[k] ?? "").trim() === "";
  const required = new Set<string>();
  for (const s of layout?.sections || []) for (const fld of s.fields) if (fld.required) required.add(fld.key);
  if (entity === "contact") {
    if (empty("firstName") && empty("lastName")) errors.firstName = "required";
    // One e-mail is enough (personal or work); the two cannot be the same address.
    if (empty("personalEmail") && empty("corporateEmail")) {
      errors.personalEmail = "oneEmail";
      errors.corporateEmail = "oneEmail";
    } else if (!empty("personalEmail") && String(draft.personalEmail).trim().toLowerCase() === String(draft.corporateEmail ?? "").trim().toLowerCase()) {
      errors.corporateEmail = "sameEmail";
    }
  } else {
    required.add("topic");
    required.add("contactId");
  }
  for (const key of required) if (empty(key)) errors[key] = "required";

  for (const def of fieldsOf(entity)) {
    if (def.readOnly || errors[def.key]) continue;
    const text = String(draft[def.key] ?? "").trim();
    if (!text) continue;
    if (def.maxLength && text.length > def.maxLength && def.type !== "phone") { errors[def.key] = "maxLength"; continue; }
    if (def.type === "email" && !isValidEmail(text)) errors[def.key] = "email";
    if (def.type === "phone" && !isValidPhone(text)) errors[def.key] = "phone";
    if (def.type === "url" && !URL_RE.test(text)) errors[def.key] = "url";
    if (def.type === "number" && !/^\d+$/.test(text)) errors[def.key] = "number";
    if (def.type === "cpf" && !isCpfChecksumValid(text)) errors[def.key] = "cpf";
    if (def.type === "cnpj" && !isCnpjValid(text)) errors[def.key] = "cnpj";
  }
  return errors;
}

/** Message key for an error code coming from the client checks or the API. */
export function errorKey(code: string):
  | "err_required" | "err_invalid" | "err_maxLength" | "err_invalidOption" | "err_phone" | "err_email" | "err_url" | "err_number"
  | "err_oneEmail" | "err_sameEmail" | "err_cpf" | "err_cnpj" {
  switch (code) {
    case "oneEmail": return "err_oneEmail";
    case "sameEmail": return "err_sameEmail";
    case "cpf": return "err_cpf";
    case "cnpj": return "err_cnpj";
    case "required": return "err_required";
    case "maxLength": return "err_maxLength";
    case "invalidOption": return "err_invalidOption";
    case "phone": return "err_phone";
    case "email": return "err_email";
    case "url": return "err_url";
    case "number": return "err_number";
    default: return "err_invalid";
  }
}

/** API "invalid" on a typed field reads better with the type-specific message. */
export function apiErrorCode(entity: CrmEntity, field: string, code: string): string {
  if (code !== "invalid") return code;
  const type = fieldDef(entity, field)?.type;
  if (type === "phone") return "phone";
  if (type === "email") return "email";
  if (type === "url") return "url";
  if (type === "number") return "number";
  if (type === "cpf") return "cpf";
  if (type === "cnpj") return "cnpj";
  return code;
}

export function newRecordDraft(entity: CrmEntity, userId: string): Draft {
  const d = toDraft(entity, {});
  d.ownerId = userId;
  d.isActive = true;
  if (entity === "lead") {
    d.status = "new";
    d.stage = "new";
  } else {
    d.preferredEmail = "personal";
  }
  return d;
}
